import React, { useState, useEffect } from 'react';
import dmCampaignService, { DMCampaign, CreateDMCampaignData } from '../../../services/dmCampaignService';
import {
  EnvelopeIcon,
  PlusIcon,
  PaperAirplaneIcon,
  TrashIcon,
  ArrowPathIcon,
  CalendarIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const DMCampaignsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<DMCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewRecipients, setPreviewRecipients] = useState<{ count: number; preview: any[] } | null>(null);
  
  const [formData, setFormData] = useState<CreateDMCampaignData>({
    name: '',
    subject: '',
    message: '',
    targetTiers: [],
    targetTags: [],
    scheduledFor: undefined,
  });

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const data = await dmCampaignService.getAll();
      setCampaigns(data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await dmCampaignService.create(formData);
      setShowCreateModal(false);
      resetForm();
      loadCampaigns();
    } catch (error) {
      console.error('Failed to create campaign:', error);
      alert('Failed to create campaign. Please try again.');
    }
  };

  const handleSendCampaign = async (id: string) => {
    if (!confirm('Are you sure you want to send this campaign? This action cannot be undone.')) return;
    try {
      await dmCampaignService.send(id);
      alert('Campaign sent successfully!');
      loadCampaigns();
    } catch (error) {
      console.error('Failed to send campaign:', error);
      alert('Failed to send campaign.');
    }
  };

  const handleDeleteCampaign = async (id: string) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await dmCampaignService.delete(id);
      loadCampaigns();
    } catch (error) {
      console.error('Failed to delete campaign:', error);
      alert('Failed to delete campaign.');
    }
  };

  const handlePreviewRecipients = async () => {
    try {
      const count = await dmCampaignService.previewRecipients({
        targetTiers: formData.targetTiers,
        targetTags: formData.targetTags,
      });
      setPreviewRecipients(count);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Failed to preview recipients:', error);
      alert('Failed to preview recipients.');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subject: '',
      message: '',
      targetTiers: [],
      targetTags: [],
      scheduledFor: undefined,
    });
  };

  const getStatusBadge = (campaign: DMCampaign) => {
    if (campaign.sentAt) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircleIcon className="h-3 w-3 mr-1" />
        Sent
      </span>;
    }
    if (campaign.scheduledFor) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        <ClockIcon className="h-3 w-3 mr-1" />
        Scheduled
      </span>;
    }
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
      Draft
    </span>;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <EnvelopeIcon className="h-10 w-10 text-indigo-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">DM Campaigns</h1>
                <p className="text-gray-600 mt-1">Send bulk messages to your patrons</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={loadCampaigns}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Campaign
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Total Campaigns</p>
            <p className="text-2xl font-bold text-gray-900">{campaigns.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Sent</p>
            <p className="text-2xl font-bold text-green-600">
              {campaigns.filter(c => c.sentAt).length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Scheduled</p>
            <p className="text-2xl font-bold text-blue-600">
              {campaigns.filter(c => c.scheduledFor && !c.sentAt).length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Drafts</p>
            <p className="text-2xl font-bold text-gray-600">
              {campaigns.filter(c => !c.scheduledFor && !c.sentAt).length}
            </p>
          </div>
        </div>

        {/* Campaigns List */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
              <span className="ml-2 text-gray-600">Loading campaigns...</span>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-12">
              <EnvelopeIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No campaigns</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating your first DM campaign</p>
              <div className="mt-6">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Create Campaign
                </button>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {campaigns.map((campaign) => (
                <div key={campaign.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
                        {getStatusBadge(campaign)}
                      </div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Subject: {campaign.subject}</p>
                      <p className="text-sm text-gray-600 line-clamp-2">{campaign.message}</p>
                      
                      <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center">
                          <UserGroupIcon className="h-4 w-4 mr-1" />
                          {campaign.totalRecipients || 0} recipients
                        </span>
                        {campaign.scheduledFor && !campaign.sentAt && (
                          <span className="flex items-center">
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            Scheduled: {new Date(campaign.scheduledFor).toLocaleString()}
                          </span>
                        )}
                        {campaign.sentAt && (
                          <span className="flex items-center">
                            <CheckCircleIcon className="h-4 w-4 mr-1" />
                            Sent: {new Date(campaign.sentAt).toLocaleString()}
                          </span>
                        )}
                      </div>
                      
                      {campaign.targetTiers && campaign.targetTiers.length > 0 && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-xs text-gray-500">Target Tiers:</span>
                          <div className="flex gap-1">
                            {campaign.targetTiers.map((tier, idx) => (
                              <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                {tier}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="ml-4 flex gap-2">
                      {!campaign.sentAt && (
                        <>
                          <button
                            onClick={() => handleSendCampaign(campaign.id)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Send campaign"
                          >
                            <PaperAirplaneIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteCampaign(campaign.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete campaign"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </>
                      )}
                      {!campaign.sentAt && (
                        <button
                          onClick={() => handleDeleteCampaign(campaign.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete campaign"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create Campaign Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowCreateModal(false)} />
              
              <div className="inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-2xl sm:w-full">
                <form onSubmit={handleCreateCampaign}>
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Create DM Campaign</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name *</label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                          placeholder="My Campaign"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                        <input
                          type="text"
                          required
                          value={formData.subject}
                          onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                          placeholder="Hello {{patron_name}}!"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                        <textarea
                          required
                          value={formData.message}
                          onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                          rows={6}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                          placeholder="Thank you for your support! Use {{patron_name}}, {{tier_name}}, {{amount}} as variables."
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Available variables: {'{{patron_name}}'}, {'{{tier_name}}'}, {'{{amount}}'}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Target Tiers (comma-separated)</label>
                        <input
                          type="text"
                          value={formData.targetTiers?.join(', ') || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            targetTiers: e.target.value.split(',').map(t => t.trim()).filter(Boolean) 
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                          placeholder="Gold, Silver, Bronze"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Target Tags (comma-separated)</label>
                        <input
                          type="text"
                          value={formData.targetTags?.join(', ') || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            targetTags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) 
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                          placeholder="vip, active, new"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Send (Optional)</label>
                        <input
                          type="datetime-local"
                          value={formData.scheduledFor ? new Date(formData.scheduledFor).toISOString().slice(0, 16) : ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            scheduledFor: e.target.value ? new Date(e.target.value).toISOString() : undefined 
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                        <button
                          type="button"
                          onClick={handlePreviewRecipients}
                          className="text-sm text-blue-700 hover:text-blue-900 font-medium"
                        >
                          Preview Recipients Count
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end gap-2">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                    >
                      Create Campaign
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Preview Recipients Modal */}
        {showPreviewModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowPreviewModal(false)} />
              
              <div className="inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-md sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                  <div className="text-center">
                    <UserGroupIcon className="mx-auto h-12 w-12 text-indigo-600" />
                    <h3 className="mt-2 text-lg font-medium text-gray-900">Recipients Preview</h3>
                    <p className="mt-2 text-3xl font-bold text-indigo-600">{previewRecipients?.count || 0}</p>
                    <p className="mt-1 text-sm text-gray-500">patrons will receive this campaign</p>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-center">
                  <button
                    onClick={() => setShowPreviewModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DMCampaignsPage;
